import styled, { css } from 'styled-components'

import Dashboard from '@/components/Dashboard'
import {
  ActionsWrapper,
  BadgesWrapper,
  Header,
  TitleWrapper,
  Toolbar,
} from '@/components/layout/page-header/page-header.styled'
import { Button } from '@/components/ui/button'
import { Collapse } from '@/components/ui/collapse'
import { media } from '@/theme/common'

// Note: no longer forces its own `height: calc(100dvh - 56px)` - this was a leftover,
// redundant override not present on any other Dashboard-based page. Dashboard.Container's own
// default `flex: 1; min-height: 0` (same as every other page) is enough on its own - Page
// already handles its own min-height/fill-to-viewport behavior (see page.tsx).
export const ScrollableDashboardContainer = styled(Dashboard.Container)``

export const AnalyticsCollapse = styled(Collapse)<{ in: boolean }>(
  ({ theme, in: isExpanded }) => css`
    flex-shrink: 0;
    transition: margin-top ${theme.transition.duration.md} ease;

    ${
      !isExpanded &&
      css`
      margin-top: calc(-1 * ${theme.space['8']});

      ${media.up('sm')} {
        margin-top: calc(-1 * ${theme.space['12']});
      }

      ${media.up('md')} {
        margin-top: calc(-1 * ${theme.space['16']});
      }
    `
    }
  `,
)

// Note: below `sm` the header regroups into 3 rows - title+toggle, the Generate Income banner,
// then Show Analytics+Add - that cuts across PageHeader's title/actions split (Show Analytics
// currently lives in `titleExtra` next to the title, Toggle/Generate Income/Add all live in
// `actions`), and 3 of the 5 items are otherwise-indistinguishable `Button` instances. CSS Grid
// with named areas lets each item move to whichever row it needs regardless of which slot it's
// still passed into, so the DOM/props above `sm` are untouched - PageHeader's own Toolbar/
// TitleWrapper/ActionsWrapper flex CSS is unaffected outside this media query, everywhere else
// that renders PageHeader is unaffected full stop (targeted by import identity, not a shared-file
// change), and nothing needed to be duplicated or reordered in reservations.page.tsx to get here.
// `display: contents` on TitleWrapper/ActionsWrapper drops just their own box from layout so their
// children promote to being direct grid items of Toolbar, placeable by grid-area.
export const ReservationsPageHeader = styled.div`
  ${media.down('sm')} {
    ${Header} {
      gap: ${({ theme }) => theme.space['8']};
    }

    ${Toolbar} {
      display: grid;
      grid-template-columns: 1fr auto;
      grid-template-areas:
        'title toggle'
        'generate generate'
        'analytics add';
      column-gap: ${({ theme }) => theme.space['8']};
      row-gap: ${({ theme }) => theme.space['8']};
    }

    ${TitleWrapper},
    ${ActionsWrapper} {
      display: contents;
    }

    ${BadgesWrapper} {
      display: none;
    }

    // Targets the title's data attribute rather than importing typography.styled's internal
    // primitive - Typography (the exported component) isn't itself a styled-components identity,
    // it's a wrapper around one, so it can't be used as a tagged-template selector here.
    [data-component-id='Typography'] {
      grid-area: title;
    }
  }
`

// justify-content: flex-end on the two right-column slots - the column itself is `auto`-width,
// sized to the wider of Toggle/Add, so without it the narrower one sits left-aligned inside that
// shared column instead of flush against the right edge like its row 1 counterpart.
export const HeaderToggleSlot = styled.div`
  ${media.down('sm')} {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    grid-area: toggle;
  }
`

export const HeaderAnalyticsSlot = styled.div`
  ${media.down('sm')} {
    display: flex;
    align-items: center;
    grid-area: analytics;
  }
`

export const HeaderAddSlot = styled.div`
  ${media.down('sm')} {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    grid-area: add;
  }
`

export const GenerateIncomeButton = styled(Button)`
  ${media.down('sm')} {
    grid-area: generate;
    width: 100%;
    justify-content: center;
  }
`
